import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { TrendingUp, TrendingDown, Coins, ArrowUpRight, ArrowDownRight, Plus } from "lucide-react";
import { toast } from "sonner";

export default function PortfolioPage() {
  const { api, user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchPortfolio = useCallback(async () => {
    try {
      const res = await api.get("/investments");
      setPortfolio(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchPortfolio();
  }, [fetchPortfolio]);

  const handleWithdraw = async (investmentId) => {
    if (!window.confirm("Retirer cette mise ?")) return;
    try {
      await api.post(`/investments/${investmentId}/withdraw`);
      refreshUser();
      fetchPortfolio();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  if (loading) {
    return (
      <div className="py-6 space-y-4">
        <div className="bg-card rounded-3xl h-32 animate-pulse" />
        <div className="bg-card rounded-3xl h-24 animate-pulse" />
      </div>
    );
  }

  const investments = portfolio?.investments || [];
  const totalInvested = portfolio?.total_invested || 0;
  const totalValue = portfolio?.total_current_value || 0;
  const totalPL = portfolio?.total_profit_loss || 0;
  const isPositive = totalPL >= 0;

  return (
    <div className="py-6 space-y-6" data-testid="portfolio-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-2xl text-foreground mb-1">Ma Constellation</h1>
          <p className="text-sm text-muted-foreground">Ton portfolio de mises sur les artistes</p>
        </div>
        <button
          onClick={() => navigate("/credits")}
          className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-[#FF6B35]/10 text-[#FF6B35] border border-[#FF6B35]/20 hover:bg-[#FF6B35]/20 transition-colors"
          data-testid="buy-credits-btn"
        >
          <Plus className="w-3 h-3" />
          Acheter credits
        </button>
      </div>

      {/* Summary */}
      <div className="bg-card border border-border rounded-3xl p-5 shadow-sm">
        <div className="grid grid-cols-3 gap-4">
          <div>
            <p className="text-xs text-muted-foreground mb-1">Investis</p>
            <p className="font-mono-credits font-bold text-lg text-card-foreground" data-testid="total-invested">
              {totalInvested}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Valeur actuelle</p>
            <p className="font-mono-credits font-bold text-lg text-card-foreground" data-testid="total-value">
              {Math.round(totalValue)}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">+/- Value</p>
            <p
              className={`font-mono-credits font-bold text-lg flex items-center gap-1 ${
                isPositive ? "text-emerald-500" : "text-red-500"
              }`}
              data-testid="total-pl"
            >
              {isPositive ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
              {isPositive ? "+" : ""}{Math.round(totalPL)}
            </p>
          </div>
        </div>
      </div>

      {/* Investments list */}
      {investments.length === 0 ? (
        <div className="text-center py-16">
          <TrendingUp className="w-14 h-14 text-muted-foreground mx-auto mb-4" />
          <h3 className="font-heading font-bold text-lg text-foreground mb-2">Aucune mise</h3>
          <p className="text-sm text-muted-foreground mb-6 max-w-xs mx-auto">
            Explore des artistes et mise des credits sur ceux qui vont percer
          </p>
          <button
            onClick={() => navigate("/discover")}
            className="inline-flex items-center gap-2 bg-[#FF6B35] text-white font-bold px-6 py-3 rounded-full shadow-[0_0_20px_rgba(255,107,53,0.4)] hover:scale-105 active:scale-95 transition-transform duration-200"
            data-testid="portfolio-discover-btn"
          >
            <Coins className="w-4 h-4" />
            Explorer
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {investments.map((inv) => {
            const pl = inv.profit_loss || 0;
            const plPct = inv.profit_loss_pct || 0;
            const isUp = pl >= 0;

            return (
              <div
                key={inv.id}
                className="bg-card border border-border rounded-3xl p-4 hover:border-[#FF6B35]/30 transition-colors duration-300 shadow-sm"
                data-testid={`investment-${inv.id}`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#FF6B35] to-[#7000FF] flex items-center justify-center shrink-0 cursor-pointer"
                    onClick={() => navigate(`/artist/${inv.artist_id}`)}
                  >
                    {inv.artist_avatar ? (
                      <img src={inv.artist_avatar} alt="" className="w-full h-full rounded-2xl object-cover" />
                    ) : (
                      <span className="text-white font-heading font-bold">
                        {(inv.artist_name || "?")[0].toUpperCase()}
                      </span>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <p
                      className="font-heading font-bold text-sm text-card-foreground truncate cursor-pointer hover:text-[#FF6B35] transition-colors"
                      onClick={() => navigate(`/artist/${inv.artist_id}`)}
                    >
                      {inv.artist_name}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs text-muted-foreground font-mono-credits">
                        {inv.amount} credits
                      </span>
                      <span className="text-xs text-muted-foreground">-</span>
                      <span className="text-xs text-muted-foreground font-mono-credits">
                        Cote: {inv.score_at_investment?.toFixed(2)} &rarr; {inv.current_score?.toFixed(2)}
                      </span>
                    </div>
                  </div>

                  <div className="text-right shrink-0">
                    <p className={`font-mono-credits font-bold text-sm ${isUp ? "text-emerald-500" : "text-red-500"}`}>
                      {isUp ? "+" : ""}{Math.round(pl)}
                    </p>
                    <p className={`text-xs font-mono-credits ${isUp ? "text-emerald-500/70" : "text-red-500/70"}`}>
                      {isUp ? "+" : ""}{plPct}%
                    </p>
                  </div>
                </div>

                <div className="mt-3 flex justify-end">
                  <button
                    onClick={() => handleWithdraw(inv.id)}
                    className="text-xs px-4 py-1.5 rounded-full bg-muted text-card-foreground/80 border border-border hover:bg-border transition-colors duration-200"
                    data-testid={`withdraw-btn-${inv.id}`}
                  >
                    Retirer ({Math.round(inv.current_value || inv.amount)} credits)
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
